import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  RootStore,
  useAppDispatch,
  useAppSelector,
} from "../../../store/store";
import {
  fetchInterviewLogs,
  fetchInterviewLogUsers,
} from "../../../store/features/AdminPanel/InterviewLogs/InterviewLogsApi";
import AllInterviewLogsStats from "./AllInterviewLogsStats";
import InterviewLogsHistoryHeader from "./InterviewLogsHistoryHeader";
import InterviewLogsHistorySearch from "./InterviewLogsHistorySearch";
import InterviewLogsHistoryCard, {
  InterviewLogRow,
} from "./InterviewLogsHistoryCard";
import InterviewLogsHistoryPagination from "./InterviewLogsHistoryPagination";
import InterviewLogsSecondaryFilters from "./InterviewLogsSecondaryFilters";
import "./interviewLogsCardView.scss";

const SEARCH_DEBOUNCE_MS = 350;

const AllInterviewLogsCardView = () => {
  const authState = useAppSelector((store: RootStore) => store.AuthSlice);
  const interviewLogsState = useAppSelector(
    (store: RootStore) => store.InterviewLogsSlice
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedInterviewUser, setSelectedInterviewUser] = useState<
    string | undefined
  >("all");
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);

  const itemsPerPage = 10;

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (!authState?.tac_user?.id && !authState?.auth_user?.id) {
      return;
    }

    const tenant = authState?.tenant || authState?.auth_user?.subdomain;
    const subdomain =
      tenant === "theagileacademy" || tenant === "aa" ? tenant : "agileacademy";

    // Fetch users for dropdown once per subdomain
    dispatch(fetchInterviewLogUsers({ subdomain }));

    const fromStr = dateFrom ? dateFrom.toISOString() : undefined;
    const toStr = dateTo
      ? new Date(
          dateTo.getFullYear(),
          dateTo.getMonth(),
          dateTo.getDate(),
          23,
          59,
          59,
          999
        ).toISOString()
      : undefined;

    const queryParams: {
      page: number;
      limit: number;
      search?: string;
      subdomain: string;
      user?: string;
      from?: string;
      to?: string;
    } = {
      page: currentPage,
      limit: itemsPerPage,
      subdomain,
    };

    if (debouncedSearch) {
      queryParams.search = debouncedSearch;
    }

    if (selectedInterviewUser && selectedInterviewUser !== "all") {
      queryParams.user = selectedInterviewUser;
    }
    if (fromStr) queryParams.from = fromStr;
    if (toStr) queryParams.to = toStr;

    dispatch(fetchInterviewLogs(queryParams));
  }, [
    authState?.tac_user?.id,
    authState?.auth_user?.id,
    authState?.tenant,
    authState?.auth_user?.subdomain,
    debouncedSearch,
    selectedInterviewUser,
    dateFrom,
    dateTo,
    currentPage,
    dispatch,
  ]);

  const paginatedRows = interviewLogsState.interviewLogs || [];

  const cardRows: InterviewLogRow[] = useMemo(
    () =>
      paginatedRows.map((interview: any) => {
        let score: number | null = null;
        if (interview.overall_score != null) {
          score = interview.overall_score;
        } else if (interview.score != null) {
          score = interview.score;
        }

        return {
          id: String(interview.id),
          role: interview.role || "",
          interviewType: interview.interviewType || "Interview",
          userName: interview.user?.name || "Unknown User",
          date: interview.date,
          duration: interview.duration || "",
          score,
        };
      }),
    [paginatedRows]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedInterviewUser, dateFrom, dateTo]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const clearAllFilters = () => {
    setSearchTerm("");
    setSelectedInterviewUser("all");
    setDateFrom(undefined);
    setDateTo(undefined);
    setCurrentPage(1);
  };

  const handleViewReport = (id: string) => {
    navigate(`/interview-logs/${id}`);
  };

  const loading = interviewLogsState.loading.fetchInterviewLogs;

  return (
    <div className="interview-logs-card-view">
      <InterviewLogsHistoryHeader />
      {/* <AllInterviewLogsStats /> */}
      <InterviewLogsHistorySearch value={searchTerm} onChange={setSearchTerm} />
      <InterviewLogsSecondaryFilters
        selectedInterviewUser={selectedInterviewUser}
        setSelectedInterviewUser={setSelectedInterviewUser}
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
        clearAllFilters={clearAllFilters}
        users={interviewLogsState.interviewLogUsers || []}
      />
      {loading ? (
        <div className="ilcv-loading">Loading interview logs...</div>
      ) : (
        <>
          {cardRows.length === 0 ? (
            <div className="ilcv-empty">
              <h2>No interviews found</h2>
              <p>Try adjusting search or filters to see more results.</p>
            </div>
          ) : (
            <div className="ilcv-card-list">
              {cardRows.map((row) => (
                <InterviewLogsHistoryCard
                  key={row.id}
                  interview={row}
                  onViewReport={handleViewReport}
                />
              ))}
            </div>
          )}
          {interviewLogsState.total > interviewLogsState.limit && (
            <InterviewLogsHistoryPagination
              currentPage={currentPage}
              totalPages={interviewLogsState.totalPages || 1}
              totalCount={interviewLogsState.total || 0}
              itemsPerPage={interviewLogsState.limit || itemsPerPage}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AllInterviewLogsCardView;
